/**
 * useIdleTimeout — client-side idle session enforcement.
 *
 * Per docs/clearpath-architecture/11-sessions-and-tokens.md §"Idle timeout",
 * each frontend surface has a per-tenant idle window:
 *
 *   main (lab staff)   default 480 min (8h)
 *   portal (customers) default 1440 min (24h)
 *   vendor (mode A)    default 480 min (8h)
 *   admin (Molaris)    default 30 min
 *   account            default 60 min
 *   JIT-elevated       15 min regardless of app
 *
 * `tenant_security_settings.idle_timeout_minutes` (surfaced as
 * `TenantSettings.idleTimeoutMinutes` on the SDL, or columns
 * `idle_timeout_minutes_{main_app,portal,admin}` in Postgres) may override
 * the app default per tenant. Callers resolve the effective minutes
 * themselves and pass it in as `idleMinutes` — this hook is presentation-
 * agnostic.
 *
 * Implementation notes:
 *   - Last-active timestamp is held in a ref (no re-renders on activity).
 *   - Activity sources: click, keydown, touchstart, mousemove (throttled
 *     to 1/sec), visibilitychange (tab returns to foreground = active).
 *   - A single 15s setInterval polls `now - lastActive`:
 *       * >= (timeout - warning) → enter warning state, fire onWarning
 *       * >= timeout            → fire onLogout (caller wipes session)
 *   - No localStorage / sessionStorage — ephemeral, per-tab. Cross-tab
 *     sync is out of scope; each tab independently decides when to warn,
 *     and the auth-service refresh cookie is the single source of truth.
 *   - Silent refresh continues regardless; the warning modal is purely UI.
 */
import type { JSX } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SignOutFailureReason } from "../sign-out.js";
import type { SignOutOptions } from "../sign-out-flow.js";

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

/** How often we poll `now - lastActive`. 15s matches the brief. */
const POLL_INTERVAL_MS = 15_000;
/** Mousemove throttle: bump lastActive at most once per second. */
const MOUSEMOVE_THROTTLE_MS = 1_000;
/** Default warning window (minutes) before forced logout. */
const DEFAULT_WARNING_MINUTES = 2;

const ACTIVITY_EVENTS = [
  "click",
  "keydown",
  "touchstart",
  "mousemove",
] as const;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseIdleTimeoutOptions {
  /** Total idle budget in minutes before forced logout. */
  idleMinutes: number;
  /** Warning window in minutes (forced logout fires `warningMinutes` after warning). Default 2. */
  warningMinutes?: number;
  /** Called once when the warning window opens. Caller renders the modal. */
  onWarning: () => void;
  /** Called once when the full idle budget elapses. This inner hook option
   *  discards the result (invoked as a bare statement), so `() => void` states
   *  that intent. Distinct from the public IdleTimeoutProvider / IdleTimeoutGuard
   *  `onLogout: (options?: SignOutOptions) => unknown`, whose result is
   *  inspected for a structured `not_signed_out` outcome and reported. */
  onLogout: () => void;
  /** Gate the hook on/off (e.g. disable while unauthenticated). Default true. */
  enabled?: boolean;
}

export interface UseIdleTimeoutResult {
  /** Reset the last-active timestamp (called by the modal's "stay" button). */
  resetActivity: () => void;
  /** Epoch-ms at which the forced logout will fire (relative to lastActive). */
  getLogoutAtMs: () => number;
  /** Epoch-ms at which the warning fires (relative to lastActive). */
  getWarnAtMs: () => number;
  /** True while within the warning window. */
  warning: boolean;
}

export function useIdleTimeout(opts: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const {
    idleMinutes,
    warningMinutes = DEFAULT_WARNING_MINUTES,
    onWarning,
    onLogout,
    enabled = true,
  } = opts;

  // Refs — no re-render on every mousemove.
  const lastActiveRef = useRef<number>(Date.now());
  const lastMoveBumpRef = useRef<number>(0);
  const warningFiredRef = useRef<boolean>(false);
  const logoutFiredRef = useRef<boolean>(false);
  const onWarningRef = useRef(onWarning);
  const onLogoutRef = useRef(onLogout);
  onWarningRef.current = onWarning;
  onLogoutRef.current = onLogout;

  // Public warning-state flag (drives the modal visibility in callers that
  // prefer pull-over-push).
  const [warning, setWarning] = useState<boolean>(false);

  const idleMs = Math.max(0, Math.floor(idleMinutes * 60_000));
  const warnMs = Math.max(0, Math.floor(warningMinutes * 60_000));

  const bumpActivity = useCallback((): void => {
    lastActiveRef.current = Date.now();
    if (warningFiredRef.current) {
      warningFiredRef.current = false;
      setWarning(false);
    }
    logoutFiredRef.current = false;
  }, []);

  const resetActivity = useCallback((): void => {
    bumpActivity();
  }, [bumpActivity]);

  const getLogoutAtMs = useCallback(
    () => lastActiveRef.current + idleMs,
    [idleMs],
  );
  const getWarnAtMs = useCallback(
    () => lastActiveRef.current + Math.max(0, idleMs - warnMs),
    [idleMs, warnMs],
  );

  // Activity listeners
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    function onActivity(e: Event): void {
      if (e.type === "mousemove") {
        const now = Date.now();
        if (now - lastMoveBumpRef.current < MOUSEMOVE_THROTTLE_MS) return;
        lastMoveBumpRef.current = now;
      }
      bumpActivity();
    }

    function onVisibility(): void {
      if (document.visibilityState === "visible") {
        bumpActivity();
      }
    }

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, bumpActivity]);

  // Poll timer
  useEffect(() => {
    if (!enabled) return;
    if (idleMs <= 0) return;

    // Reset fire-flags + lastActive on (re)enable or config change.
    bumpActivity();

    const id = setInterval(() => {
      const since = Date.now() - lastActiveRef.current;
      if (since >= idleMs) {
        if (!logoutFiredRef.current) {
          logoutFiredRef.current = true;
          try {
            onLogoutRef.current();
          } catch {
            // swallow — caller shouldn't throw
          }
        }
        return;
      }
      if (since >= idleMs - warnMs) {
        if (!warningFiredRef.current) {
          warningFiredRef.current = true;
          setWarning(true);
          try {
            onWarningRef.current();
          } catch {
            // swallow
          }
        }
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(id);
    };
  }, [enabled, idleMs, warnMs, bumpActivity]);

  return useMemo(
    () => ({ resetActivity, getLogoutAtMs, getWarnAtMs, warning }),
    [resetActivity, getLogoutAtMs, getWarnAtMs, warning],
  );
}

// ---------------------------------------------------------------------------
// Provider + context
// ---------------------------------------------------------------------------

export interface IdleTimeoutContextValue {
  /** Effective idle minutes in force (post-override, post-JIT). */
  idleMinutes: number;
  /** Warning-window minutes. */
  warningMinutes: number;
  /** True while the warning modal should be visible. */
  warning: boolean;
  /** Call when the user clicks "stay signed in" — resets activity. */
  stayActive: () => void;
  /** Call when the user clicks "sign out now" — forces immediate logout. */
  signOutNow: () => void;
  /** ms until forced logout, recomputed on demand. */
  getLogoutAtMs: () => number;
}

const IdleTimeoutContext = createContext<IdleTimeoutContextValue | null>(null);

export interface IdleTimeoutProviderProps {
  /** Effective idle budget (minutes). Resolve tenant-override + JIT outside. */
  idleMinutes: number;
  /** Warning window (minutes). Default 2. */
  warningMinutes?: number;
  /** Gate the whole thing (e.g. disable while session is loading). Default true. */
  enabled?: boolean;
  /** Called when the idle budget elapses (`trigger: "idle"`) or the user clicks
   *  "sign out now" (`trigger: "user"`); must tear down the session. It receives
   *  `SignOutOptions` carrying that trigger and a pre-navigation failure hook, so
   *  pass `signOut` straight through. A structured `not_signed_out` result is
   *  sanitized and reported, while other returns are discarded. The `unknown`
   *  return accepts value-returning signOut callbacks; don't narrow it to
   *  `Promise<void> | void` — a union with `void` loses TS's void-return relaxation. */
  onLogout: (options?: SignOutOptions) => unknown;
  /** Receives a sanitized failure (including its trigger) when the sign-out
   *  resolves unsuccessfully or the callback rejects. At most one per attempt.
   *  When omitted, a structured console warning is emitted so the failure is
   *  never silently swallowed. */
  onLogoutFailure?: (failure: IdleLogoutFailure) => void;
  /**
   * Called when the warning fires — typically a no-op (context.warning
   * drives the modal). Provided for telemetry hooks.
   */
  onWarning?: () => void;
  /**
   * Called when the user confirms "stay signed in". Typically triggers a
   * fresh silent refresh so the access token is maximally young. Caller
   * passes `() => authClient.getAccessToken()` or similar.
   */
  onStay?: () => void | Promise<void>;
  children: ReactNode;
}

/** The idle guard's own trigger (its timer, or the modal's "sign out now"
 *  button). The guard may join a sign-out already in flight, in which case
 *  this is still the guard's trigger, not the in-flight caller's. */
type IdleLogoutTrigger = NonNullable<SignOutOptions["trigger"]>;

export type IdleLogoutFailure =
  | {
      kind: "not_signed_out";
      reason: SignOutFailureReason;
      status?: number;
      trigger: IdleLogoutTrigger;
    }
  | { kind: "callback_rejected"; trigger: IdleLogoutTrigger };

const SIGN_OUT_FAILURE_REASONS = {
  network: true,
  forbidden_origin: true,
  invalid_request: true,
  server_unavailable: true,
  unexpected: true,
  session_still_live: true,
} satisfies Record<SignOutFailureReason, true>;

function defaultIdleLogoutFailureReporter(failure: IdleLogoutFailure): void {
  try {
    console.warn(`[auth-client] idle-guard sign-out failed (trigger: ${failure.trigger})`, failure);
  } catch {
    // Observability must never interfere with local session teardown.
  }
}

function reportIdleLogoutFailure(
  failure: IdleLogoutFailure,
  onFailure?: (failure: IdleLogoutFailure) => void,
): void {
  try {
    (onFailure ?? defaultIdleLogoutFailureReporter)(failure);
  } catch {
    // A broken application telemetry sink must not escape the timeout path.
  }
}

function unexpectedSignOutFailure(trigger: IdleLogoutTrigger): IdleLogoutFailure {
  return { kind: "not_signed_out", reason: "unexpected", trigger };
}

function toIdleLogoutFailure(
  value: unknown,
  trigger: IdleLogoutTrigger,
  assumeNotSignedOut = false,
): IdleLogoutFailure | null {
  if (typeof value !== "object" || value === null) {
    return assumeNotSignedOut ? unexpectedSignOutFailure(trigger) : null;
  }
  const candidate = value as {
    verdict?: unknown;
    reason?: unknown;
    status?: unknown;
  };
  let isNotSignedOut = assumeNotSignedOut;
  try {
    if (!isNotSignedOut) {
      if (candidate.verdict !== "not_signed_out") return null;
      isNotSignedOut = true;
    }

    const candidateReason = candidate.reason;
    const reason =
      typeof candidateReason === "string" &&
      Object.prototype.hasOwnProperty.call(SIGN_OUT_FAILURE_REASONS, candidateReason)
        ? (candidateReason as SignOutFailureReason)
        : "unexpected";

    const failure: IdleLogoutFailure = {
      kind: "not_signed_out",
      reason,
      trigger,
    };
    const candidateStatus = candidate.status;
    if (
      typeof candidateStatus === "number" &&
      Number.isInteger(candidateStatus) &&
      candidateStatus >= 0 &&
      candidateStatus <= 599
    ) {
      failure.status = candidateStatus;
    }
    return failure;
  } catch {
    return isNotSignedOut
      ? unexpectedSignOutFailure(trigger)
      : { kind: "callback_rejected", trigger };
  }
}

/** Execute an idle-guard sign-out (timer or "sign out now") without blocking
 * the caller, while retaining only the bounded fields needed for operational
 * diagnosis. At most one failure is reported per attempt: once the
 * pre-navigation hook has reported, a later resolution or rejection of the
 * callback is not reported again. The returned promise settles after outcome
 * reporting so providers can coalesce repeated actions during the same attempt.
 *
 * Module-internal: exported only for its unit tests, not from any barrel. */
export function executeIdleLogout(
  onLogout: (options?: SignOutOptions) => unknown,
  onFailure?: (failure: IdleLogoutFailure) => void,
  requestedTrigger?: IdleLogoutTrigger,
): Promise<void> {
  // Normalize here, not at call sites: anything other than "user" (including
  // a stray click event) is "idle", so the reported trigger stays two-valued.
  const trigger: IdleLogoutTrigger = requestedTrigger === "user" ? "user" : "idle";
  let preNavigationFailureObserved = false;
  let outcome: unknown;
  try {
    outcome = onLogout({
      trigger,
      onFailureBeforeNavigate: (result) => {
        preNavigationFailureObserved = true;
        const failure = toIdleLogoutFailure(result, trigger, true);
        if (failure) reportIdleLogoutFailure(failure, onFailure);
      },
    });
  } catch {
    if (!preNavigationFailureObserved) {
      reportIdleLogoutFailure({ kind: "callback_rejected", trigger }, onFailure);
    }
    return Promise.resolve();
  }

  return Promise.resolve(outcome)
    .then(
      (result) => {
        if (preNavigationFailureObserved) return;
        const failure = toIdleLogoutFailure(result, trigger);
        if (failure) reportIdleLogoutFailure(failure, onFailure);
      },
      () => {
        if (preNavigationFailureObserved) return;
        reportIdleLogoutFailure({ kind: "callback_rejected", trigger }, onFailure);
      },
    )
    .catch(() => {
      // Last-resort containment for hostile thenables/proxies or future
      // normalization regressions: the exported executor never rejects.
      if (!preNavigationFailureObserved) {
        reportIdleLogoutFailure({ kind: "callback_rejected", trigger }, onFailure);
      }
    });
}

export function IdleTimeoutProvider({
  idleMinutes,
  warningMinutes = DEFAULT_WARNING_MINUTES,
  enabled = true,
  onLogout,
  onLogoutFailure,
  onWarning,
  onStay,
  children,
}: IdleTimeoutProviderProps): JSX.Element {
  const onLogoutRef = useRef(onLogout);
  const onLogoutFailureRef = useRef(onLogoutFailure);
  const onStayRef = useRef(onStay);
  const onWarningRef = useRef(onWarning);
  const logoutInFlightRef = useRef(false);
  onLogoutRef.current = onLogout;
  onLogoutFailureRef.current = onLogoutFailure;
  onStayRef.current = onStay;
  onWarningRef.current = onWarning;

  // The inner timer hook calls this with no argument, so the timer path is
  // "idle"; only signOutNow passes "user". executeIdleLogout normalizes it.
  const handleLogout = useCallback((trigger?: IdleLogoutTrigger) => {
    if (logoutInFlightRef.current) return;
    logoutInFlightRef.current = true;
    void executeIdleLogout(
      onLogoutRef.current,
      onLogoutFailureRef.current,
      trigger,
    ).then(
      () => {
        // A rejected or otherwise completed callback may leave the current page
        // active. Release the guard so a later deliberate attempt remains live.
        logoutInFlightRef.current = false;
      },
      () => {
        // Defensive containment: executeIdleLogout is total, but never strand
        // the provider latch if a future implementation accidentally rejects.
        logoutInFlightRef.current = false;
      },
    );
  }, []);
  const handleWarning = useCallback(() => {
    onWarningRef.current?.();
  }, []);

  const { warning, resetActivity, getLogoutAtMs } = useIdleTimeout({
    idleMinutes,
    warningMinutes,
    onWarning: handleWarning,
    onLogout: handleLogout,
    enabled,
  });

  const stayActive = useCallback(() => {
    resetActivity();
    void Promise.resolve(onStayRef.current?.()).catch(() => {
      /* swallow — best-effort silent refresh */
    });
  }, [resetActivity]);

  // Zero-arg on purpose: the modal wires it as `onClick={signOutNow}`, so any
  // argument it received would be the click event, not a trigger.
  const signOutNow = useCallback(() => {
    handleLogout("user");
  }, [handleLogout]);

  const value = useMemo<IdleTimeoutContextValue>(
    () => ({
      idleMinutes,
      warningMinutes,
      warning,
      stayActive,
      signOutNow,
      getLogoutAtMs,
    }),
    [idleMinutes, warningMinutes, warning, stayActive, signOutNow, getLogoutAtMs],
  );

  return (
    <IdleTimeoutContext.Provider value={value}>
      {children}
    </IdleTimeoutContext.Provider>
  );
}

export function useIdleTimeoutContext(): IdleTimeoutContextValue {
  const ctx = useContext(IdleTimeoutContext);
  if (!ctx) {
    throw new Error(
      "useIdleTimeoutContext() must be used inside <IdleTimeoutProvider>",
    );
  }
  return ctx;
}

/**
 * Resolve the effective idle minutes for a given app, applying (in order):
 *   1. JIT override (15 min) when session.isJitGrant is true
 *   2. Tenant override (idleTimeoutMinutes) when non-null
 *   3. App default passed by the caller
 *
 * Pure function — unit-testable.
 */
export interface ResolveIdleMinutesInput {
  appDefaultMinutes: number;
  tenantOverrideMinutes?: number | null;
  isJitGrant?: boolean | null;
  /** JIT budget; default 15 per bible. */
  jitMinutes?: number;
}

export function resolveIdleMinutes(input: ResolveIdleMinutesInput): number {
  const jitMinutes = input.jitMinutes ?? 15;
  if (input.isJitGrant) return jitMinutes;
  if (typeof input.tenantOverrideMinutes === "number" && input.tenantOverrideMinutes > 0) {
    return input.tenantOverrideMinutes;
  }
  return input.appDefaultMinutes;
}
